import { convertGeometryToWkt, FieldsMap, FieldViewModel, FieldViewModelImpl, Geometry2, getCroppingSiblings, ICropping, IOmniaSettings, map } from "../../omnia"
import { commands } from "../../strings"
import { BoundaryDrawingContext } from "../boundary/drawingContext"

/// <reference path="../boundary/drawingContext.ts" />

const cmd = commands

const msgRemoveOverlaps = "Remove overlaps"

import IMapTopologyService = omnia.map.IMapTopologyService
import { ol_Feature, ol_geom } from "@omnia/exports/openlayers"

export class CroppingDrawingContext extends BoundaryDrawingContext {

    public readonly commands = [
        { label: msgRemoveOverlaps, execute: () => this.removeOverlaps() }
    ]

    constructor(
        private cropping: ICropping,
        private getPrevious: () => ICropping,
        map: FieldsMap,
        topo: IMapTopologyService,
        switchToMapView: () => void,
        settings: IOmniaSettings
    ) {

        super(
            map,
            topo,
            switchToMapView,
            map.croppingLayer,
            settings,
            cropping?.BoundaryArea, {
            copy: a => this.cropping.Area = a,
            enabled: true
        }
        )

        this.setupTarget(true, cropping)

    }

    getEditCommandText(): string {
        return this.newBoundary ? cmd.drawCropBoundary : cmd.editCropBoundary
    }

    protected updateFeatureGeometry(feature: any): boolean {

        if (feature && this._fieldFeature) {

            let geom = feature.getGeometry()

            const intersection = this.topo.intersection(this._fieldFeature.getGeometry(), geom, true)

            if (!intersection) {
                return true
            }

            geom = intersection.getOpenLayersGeometry()

            if (geom && geom.getArea) {
                feature.setGeometry(geom)
                return true
            }

        }

        return null;

    }

    createDefaultFeature() {

        let geometry: any

        const cropping = this.cropping
        const field = cropping.Field
        const topo = this.topo

        const fieldFeature = this.findFeature(false, field)
        if (!fieldFeature) return

        if (cropping.Boundary) {
            geometry = getGeometry(topo, { Boundary: cropping.Boundary })
        }

        if (!geometry) {

            const prev = this.getPrevious ? this.getPrevious() : null

            if (topo && prev && prev.Boundary) {
                geometry = getGeometry(topo, prev)
            }

        }

        if ((!geometry) && fieldFeature) {

            const concurrent = getConcurrentCrops(cropping)

            if (concurrent.length) {

                const ccgeom = concurrent.map(s => getGeometry(topo, s)).filter(f => !!f)

                geometry = getLargestUncoveredArea(fieldFeature, ccgeom, topo)

            }

            geometry = geometry || fieldFeature.getGeometry().clone()

        }


        if (geometry) {

            var croppingVm = new FieldViewModelImpl(cropping, 0, () => true, <FieldViewModel>{});

            var feature = new ol_Feature({ geometry: geometry });
            feature.set("data", croppingVm);

            this._layer.setupNewTargetFeature(feature, true);

            return feature;

        }

    }

    protected validateInternal(feature: any): boolean {

        let cropGeometry = feature.getGeometry();

        if (!this.validateConcurrentCrops(cropGeometry)) {
            return false
        }

        if (!this.validateFieldBoundary(cropGeometry)) {
            return false
        }

        return true

    }


    private validateFieldBoundary(croppingGeometry: ol_geom.Geometry) {

        const { intersection, error } = this.topo.intersection2(this._fieldFeature.getGeometry(), croppingGeometry, true)

        // 210905 - If the intersection cannot be computed, then avoid treating this as an error
        if (error) {
            return true
        }

        const area = intersection?.getArea()

        if (area) {
            return true
        }

        const msgCroppingOutsideField = "Invalid boundary: it has no intersection with the field boundary"

        this.addValidationMessage(msgCroppingOutsideField)

        return false

    }

    private validateConcurrentCrops(croppingGeometry: ol_geom.Geometry): boolean {

        var crops = this.getInvalidSiblings(croppingGeometry);

        if (crops.length) {
            const msgOtherCroppingCovered = "Invalid boundary: some of the other cropping in the field would be completely covered"
            this.addValidationMessage(msgOtherCroppingCovered)
            return false
        }

        return true

    }

    private getInvalidSiblings(croppingGeometry: any): ICropping[] {

        var siblings = getCroppingSiblings(this.cropping, false);

        siblings = siblings.filter(s => !!s.Boundary);

        var diffs = siblings.map(s => {

            const otherGeometry = convertGeometryToWkt(s.Boundary);

            try {

                const diff = this.topo.difference(otherGeometry, [croppingGeometry]).getOpenLayersGeometryWithArea();

                if (!diff) {
                    return s
                }

            } catch (error) {
                console.log(error)
            }

        });

        diffs = diffs.filter(s => !!s);

        return diffs;


    }

    canEditBoundary() {

        return this.checkFieldBoundary()

    }

    removeOverlaps() {
        const { map, feature, cropping, topo } = this
        removeOverlaps(map, feature, cropping, topo)
        this.updateFeatureGeometry(feature)
    }

}


function getLargestUncoveredArea(fieldFeature: any, others: ol_geom.Geometry[], topo: IMapTopologyService) {

    const fieldGeometry = fieldFeature.getGeometry().clone()

    if (!others.length) {
        return fieldFeature.getGeometry()
    }

    const r = topo.difference(fieldGeometry, others)

    return r ? r.getOpenLayersGeometry() : null

}

function getGeometry(topo: IMapTopologyService, from: { Boundary: Geometry2 }) {

    const b = from ? from.Boundary : null
    if (!b) return

    const t = topo.getGeometry(b)
    if (!t) return

    return t.getOpenLayersGeometry()

}

function getConcurrentCrops(target: ICropping) {

    const get = omnia.fields.getConcurrentCrops

    const sameYear = target.Field.Cropping.filter(c => c.YearId == target.YearId)

    const cc = sameYear.length
        ? get(target, ({ cropping: sameYear }))
        : get(target)

    return cc

}

function removeOverlaps(map: FieldsMap, feature: ol_Feature, cropping: ICropping, topo: IMapTopologyService) {

    const { FieldId: fieldId, CroppingId: croppingId } = cropping

    let targetGeometry = topo.makeValid(feature.getGeometry())

    const others = map.croppingLayer
        .findFeatures(f => {
            const d = f.get("data") as FieldViewModel
            return d && (d.fieldId === fieldId) && (d.croppingId !== croppingId)
        })
        .map(f => f.getGeometry())
        .map(g => topo.makeValid(g))

    if (others.length) {
        targetGeometry = topo.difference(targetGeometry, others)
        targetGeometry = topo.getLargestPolygon(targetGeometry)
    }

    const ol = targetGeometry?.getOpenLayersGeometry()

    if (ol) {
        feature.setGeometry(ol)
    }

}
